import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  X, Search, ChevronRight, ChevronLeft, MapPin, Sparkles, Stethoscope, UserRound, FlaskConical,
  Mic, MicOff,
} from "lucide-react";
import { api, PRIORITY_LABELS, professionalTitle, formatApiErrorDetail } from "@/lib/api";
import SynapseLoader from "@/components/SynapseLoader";
import { toast } from "sonner";

/* ----------------- Configurazione flusso "Crea una sinapsi" ----------------- */

// Step 1: con chi vuoi connetterti
const SYNAPSES = [
  {
    key: "mmg",
    role: "medico_medicina_generale",
    label: "MEDICO DI MEDICINA GENERALE",
    short: "MMG",
    icon: UserRound,
    color: "from-sky-500 to-blue-600",
  },
  {
    key: "specialista",
    role: "medico_specialista",
    label: "MEDICO SPECIALISTA",
    short: "Specialista",
    icon: Stethoscope,
    color: "from-[#1E3A8A] to-indigo-600",
  },
  {
    key: "diagnostica",
    role: "diagnostica",
    label: "DIAGNOSTICA E LABORATORIO",
    short: "Diagnostica",
    icon: FlaskConical,
    color: "from-emerald-500 to-teal-600",
  },
];

// Step 2: sottocategorie in base al destinatario
const REASONS = {
  mmg: [
    { key: "richiesta_prescrizione", label: "Richiesta prescrizione", hint: "Farmaci o esami da prescrivere al paziente." },
    { key: "richiesta_chiarimento",  label: "Chiarimenti",             hint: "Domande su terapie in corso, allergie, storia clinica." },
    { key: "generica",               label: "Comunicazione",           hint: "Nota informativa o aggiornamento sul paziente." },
  ],
  specialista: [
    { key: "richiesta_consulto",     label: "Richiesta consulto",      hint: "Chiedi un parere specialistico sul caso." },
    { key: "richiesta_chiarimento",  label: "Chiarimenti",             hint: "Approfondisci un referto o una diagnosi." },
    { key: "generica",               label: "Comunicazione",           hint: "Nota informativa o aggiornamento clinico." },
  ],
  diagnostica: [
    { key: "richiesta_integrazione", label: "Richiesta integrazione",  hint: "Aggiungi esami o accertamenti a una richiesta esistente." },
    { key: "richiesta_informazione", label: "Richiesta informazioni",  hint: "Chiedi dettagli su un risultato o su modalità di prelievo." },
  ],
};

function ContactAvatar({ contact, size = "md" }) {
  const displayName = contact?.name || `${contact?.first_name || ""} ${contact?.last_name || ""}`.trim() || contact?.email || "?";
  const initials = displayName.split(" ").slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  const sizes = { sm: "h-6 w-6 text-[9px]", md: "h-9 w-9 text-[11px]" };
  return (
    <div className={`${sizes[size]} rounded-full overflow-hidden bg-gradient-to-br from-[#1E3A8A] to-[#10B981] text-white flex items-center justify-center font-heading font-bold flex-shrink-0`}>
      {contact?.avatar_url ? <img src={contact.avatar_url} alt="" className="h-full w-full object-cover" /> : (initials || "?")}
    </div>
  );
}

export default function ComposeDialog({ open, onOpenChange, onCreated, initialRecipient }) {
  const [step, setStep] = useState(1); // 1 sinapsi, 2 motivo, 3 dettagli+AI
  const [synapseKey, setSynapseKey] = useState(null);
  const [reasonKey, setReasonKey] = useState(null);
  const [priority, setPriority] = useState("normale");
  const [subject, setSubject] = useState("");
  const [pfirst, setPfirst] = useState("");
  const [plast, setPlast] = useState("");
  const [pbirth, setPbirth] = useState("");
  const [pref, setPref] = useState("");
  const [pemail, setPemail] = useState("");
  const [sendToPatient, setSendToPatient] = useState(false);
  const [rawText, setRawText] = useState("");
  const [body, setBody] = useState("");
  const [aiApplied, setAiApplied] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiWarnings, setAiWarnings] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [query, setQuery] = useState("");
  const [provinceFilter, setProvinceFilter] = useState("all");
  const [provinces, setProvinces] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // Dettatura vocale (Web Speech API) — robusta anti-duplicazione (mobile-safe)
  const [listening, setListening] = useState(false);
  const recogRef = React.useRef(null);
  const userStopRef = React.useRef(false);
  const finalBaseRef = React.useRef("");
  const processedFinalsRef = React.useRef(new Set());
  // ultimo chunk finale appeso (per dedupe testuale cross-session su Android)
  const lastAppendedRef = React.useRef("");
  const sessionActiveRef = React.useRef(false);
  const speechSupported = typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);
  const isMobile = typeof navigator !== "undefined" &&
    /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");

  // Normalizza per confronti testuali (lower, no punteggiatura, spazi collassati)
  const normText = (s) => (s || "")
    .toLowerCase()
    .replace(/[.,;:!?…"'`()\[\]{}]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  // True se `chunk` è già presente in coda a `base` (esatto, o come suffisso overlap)
  const isTailDuplicate = (base, chunk) => {
    const b = normText(base);
    const c = normText(chunk);
    if (!c) return true;
    if (!b) return false;
    // 1) chunk identico all'ultimo appeso → duplicato di replay
    if (normText(lastAppendedRef.current) === c) return true;
    // 2) coda della base termina esattamente con chunk (accodamento doppio dopo restart)
    if (b.endsWith(c)) return true;
    // 3) overlap parziale: se la coda di `base` contiene le stesse ultime ~8 parole di chunk
    const cWords = c.split(" ");
    if (cWords.length >= 3) {
      const tail = cWords.slice(-Math.min(8, cWords.length)).join(" ");
      if (b.endsWith(tail)) return true;
    }
    return false;
  };

  // Whisper server fallback (per browser senza Web Speech API, es. Firefox desktop)
  const [whisperBusy, setWhisperBusy] = useState(false);
  const [whisperRecording, setWhisperRecording] = useState(false);
  const mediaRecorderRef = React.useRef(null);
  const audioChunksRef = React.useRef([]);
  const mediaStreamRef = React.useRef(null);

  // Ref al submit per poterlo invocare da un comando vocale ("invia sinapsi")
  const submitRef = React.useRef(null);

  // ------ Comandi vocali rapidi ------
  // Analizza il testo appena finalizzato per estrarre comandi (priorità/email/invia/cancella).
  // Ritorna il testo con i comandi rimossi (così non finiscono nella richiesta).
  const parseAndApplyCommands = (chunk) => {
    if (!chunk) return chunk;
    let text = " " + chunk + " ";

    // Priorità: "priorità critica/alta/normale/bassa" o "urgenza critica/alta/normale"
    const prio = text.match(/\b(?:priorit[àa]|urgenza)\s+(critica|critico|alta|alto|normale|bassa|basso|urgente)\b/i);
    if (prio) {
      const map = { critica: "critica", critico: "critica", urgente: "critica",
                    alta: "alta", alto: "alta",
                    normale: "normale", bassa: "bassa", basso: "bassa" };
      const newP = map[prio[1].toLowerCase()];
      if (newP) {
        setPriority(newP);
        toast.success(`Priorità impostata: ${newP}`);
        text = text.replace(prio[0], " ");
      }
    }

    // Email paziente: "email paziente foo chiocciola bar punto it"
    // Prima variante: se pronunciata come stringa email vera "email X@Y.Z"
    const emailLiteral = text.match(/\b(?:email(?:\s+paziente)?)\s+([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})\b/i);
    if (emailLiteral) {
      setPemail(emailLiteral[1].toLowerCase());
      toast.success(`Email paziente: ${emailLiteral[1].toLowerCase()}`);
      text = text.replace(emailLiteral[0], " ");
    } else {
      // Seconda variante: "email X chiocciola Y punto Z" (Italian spoken form)
      const spoken = text.match(/\bemail(?:\s+paziente)?\s+([\w.\-]+)\s+chiocciola\s+([\w.\-]+)\s+punto\s+([\w]{2,10})\b/i);
      if (spoken) {
        const addr = `${spoken[1]}@${spoken[2]}.${spoken[3]}`.toLowerCase();
        setPemail(addr);
        toast.success(`Email paziente: ${addr}`);
        text = text.replace(spoken[0], " ");
      }
    }

    // Cancella tutto: "cancella tutto" / "resetta testo"
    if (/\b(?:cancella tutto|resetta testo|pulisci testo)\b/i.test(text)) {
      finalBaseRef.current = "";
      processedFinalsRef.current = new Set();
      setRawText("");
      toast.success("Testo cancellato");
      return "";
    }

    // Invia: "invia sinapsi" / "invia adesso" / "invia messaggio"
    if (/\b(?:invia sinapsi|invia adesso|invia ora|invia messaggio|invia richiesta)\b/i.test(text)) {
      text = text.replace(/\b(?:invia sinapsi|invia adesso|invia ora|invia messaggio|invia richiesta)\b/i, " ");
      // Ferma la dettatura e triggera submit
      userStopRef.current = true;
      try { recogRef.current?.stop(); } catch {}
      setTimeout(() => {
        try { submitRef.current && submitRef.current(); } catch {}
      }, 250);
      toast.info("Comando riconosciuto: invio in corso…");
    }

    return text.trim();
  };

  const toggleDictation = () => {
    if (!speechSupported) {
      toast.error("Dettatura non supportata da questo browser. Usa Chrome, Edge o Safari iOS aggiornato.");
      return;
    }
    if (listening) {
      // Stop richiesto dall'utente: NO auto-restart
      userStopRef.current = true;
      try { recogRef.current?.stop(); } catch {}
      return;
    }
    // Reset stato dettatura per una nuova sessione
    userStopRef.current = false;
    processedFinalsRef.current = new Set();
    lastAppendedRef.current = "";
    finalBaseRef.current = rawText ? rawText.trimEnd() + " " : "";

    const R = window.SpeechRecognition || window.webkitSpeechRecognition;
    const startRecognition = () => {
      const r = new R();
      r.lang = "it-IT";
      r.interimResults = true;
      // Su mobile `continuous=true` è instabile su Android Chrome (auto-restart
      // che rispedisce l'ultimo risultato come "nuovo"). Usiamo continuous=false
      // + restart manuale in onend: OGNI utterance è una sessione pulita.
      r.continuous = !isMobile;
      r.maxAlternatives = 1;

      r.onstart = () => {
        sessionActiveRef.current = true;
        // Nuova sessione → indici finali ripartono da 0, resetta il Set
        processedFinalsRef.current = new Set();
        setListening(true);
      };

      r.onerror = (e) => {
        // "no-speech" e "aborted" sono benigni: onend gestirà l'auto-restart se serve
        if (e.error === "not-allowed" || e.error === "service-not-allowed") {
          userStopRef.current = true;
          setListening(false);
          toast.error("Microfono bloccato. Autorizza il microfono nelle impostazioni del browser.");
        }
      };

      r.onend = () => {
        sessionActiveRef.current = false;
        // Auto-restart solo se l'utente NON ha premuto stop
        if (!userStopRef.current) {
          // Piccolo delay per lasciare al motore il tempo di rilasciare le risorse
          // (particolarmente importante su Android)
          setTimeout(() => {
            if (userStopRef.current || sessionActiveRef.current) return;
            try { r.start(); } catch {
              // Se la vecchia istanza non si riavvia, ne creiamo una nuova
              try { startRecognition(); } catch {}
            }
          }, isMobile ? 300 : 50);
          return;
        }
        setListening(false);
      };

      r.onresult = (ev) => {
        let interim = "";
        let newFinals = "";
        for (let i = 0; i < ev.results.length; i++) {
          const t = (ev.results[i][0]?.transcript || "");
          if (ev.results[i].isFinal) {
            if (!processedFinalsRef.current.has(i)) {
              processedFinalsRef.current.add(i);
              const cand = t.trim();
              // Difesa aggiuntiva: se il chunk è già in coda alla base → duplicato
              if (cand && !isTailDuplicate(finalBaseRef.current, cand)) {
                newFinals += cand + " ";
                lastAppendedRef.current = cand;
              }
            }
          } else {
            interim += t;
          }
        }
        if (newFinals) {
          // Estrae comandi dal chunk finale e rimuove il testo comando dal transcript
          const cleaned = parseAndApplyCommands(newFinals);
          if (cleaned) finalBaseRef.current += cleaned + " ";
        }
        const merged = (finalBaseRef.current + interim).replace(/\s+/g, " ").trim();
        setRawText(merged);
      };

      recogRef.current = r;
      try { r.start(); } catch { /* già attivo o non pronto */ }
    };
    startRecognition();
  };

  // ------ Whisper fallback (MediaRecorder → POST /api/ai/transcribe) ------
  const toggleWhisperDictation = async () => {
    if (whisperRecording) {
      try { mediaRecorderRef.current?.stop(); } catch {}
      return;
    }
    if (!navigator?.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Il browser non supporta la registrazione audio.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      // Scegli il primo mimeType supportato dal browser
      const mimeCandidates = [
        "audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4",
      ];
      const mime = mimeCandidates.find((m) => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) || "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        setWhisperRecording(false);
        try { mediaStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 500) {
          toast.error("Registrazione troppo breve, riprova.");
          return;
        }
        setWhisperBusy(true);
        try {
          const fd = new FormData();
          const ext = (mr.mimeType || "audio/webm").includes("mp4") ? "mp4"
                    : (mr.mimeType || "audio/webm").includes("ogg") ? "ogg" : "webm";
          fd.append("file", blob, `speech.${ext}`);
          const { data } = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          const text = (data?.text || "").trim();
          if (!text) { toast.info("Nessun parlato riconosciuto."); return; }
          // Applica comandi + accoda al rawText
          const cleaned = parseAndApplyCommands(text);
          if (cleaned) {
            setRawText((prev) => (prev ? prev.trimEnd() + " " : "") + cleaned);
            toast.success("Trascrizione completata (Whisper)");
          }
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore trascrizione");
        } finally { setWhisperBusy(false); }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setWhisperRecording(true);
    } catch (e) {
      toast.error("Impossibile accedere al microfono. Autorizzalo nelle impostazioni.");
    }
  };

  React.useEffect(() => () => {
    userStopRef.current = true;
    try { recogRef.current?.stop(); } catch {}
    try { mediaRecorderRef.current?.stop(); } catch {}
    try { mediaStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
  }, []);

  useEffect(() => {
    if (!open) return;
    // Ferma qualsiasi dettatura in corso quando si riapre/reset il dialog
    userStopRef.current = true;
    try { recogRef.current?.stop(); } catch {}
    setListening(false);
    processedFinalsRef.current = new Set();
    lastAppendedRef.current = "";
    finalBaseRef.current = "";
    // Se c'è un destinatario iniziale (da Rubrica) salto direttamente allo step motivo
    let sKey = null;
    if (initialRecipient) {
      const p = initialRecipient.profession || initialRecipient.role;
      if (p === "medico_medicina_generale") sKey = "mmg";
      else if (p === "medico_specialista") sKey = "specialista";
      else if (p === "laboratorio" || p === "diagnostica") sKey = "diagnostica";
    }
    setSynapseKey(sKey);
    setStep(sKey ? 2 : 1);
    setReasonKey(null);
    setPriority("normale");
    setRecipients(initialRecipient ? [initialRecipient] : []);
    setSubject(""); setPfirst(""); setPlast(""); setPbirth(""); setPref(""); setPemail(""); setSendToPatient(false);
    setRawText(""); setBody(""); setAiApplied(false); setAiWarnings([]);
    setQuery(""); setProvinceFilter("all");
    api.get("/contacts/provinces").then(({ data }) => setProvinces(data)).catch(() => {});
  }, [open, initialRecipient]);

  // Ricerca contatti filtrata per il tipo di sinapsi scelto (allo step 3).
  // Nessun risultato mostrato finché l'utente non inizia a scrivere un nome
  // o non seleziona una provincia — per evitare "elenco pre-popolato" indesiderato.
  useEffect(() => {
    if (!open || step !== 3 || !synapseKey) return;
    const hasQuery = (query || "").trim().length > 0;
    const hasProvince = provinceFilter && provinceFilter !== "all";
    if (!hasQuery && !hasProvince) { setContacts([]); return; }
    const syn = SYNAPSES.find((s) => s.key === synapseKey);
    const t = setTimeout(async () => {
      try {
        const params = {};
        if (query) params.q = query;
        if (provinceFilter && provinceFilter !== "all") params.province = provinceFilter;
        // Diagnostica include sia laboratorio che diagnostica: chiamiamo entrambi
        if (syn.key === "diagnostica") {
          const [a, b] = await Promise.all([
            api.get("/contacts", { params: { ...params, role: "laboratorio" } }),
            api.get("/contacts", { params: { ...params, role: "diagnostica" } }),
          ]);
          setContacts([...(a.data || []), ...(b.data || [])]);
        } else {
          params.role = syn.role;
          const { data } = await api.get("/contacts", { params });
          setContacts(data);
        }
      } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [query, provinceFilter, open, step, synapseKey]);

  const currentSynapse = SYNAPSES.find((s) => s.key === synapseKey);
  const currentReason  = synapseKey && reasonKey ? REASONS[synapseKey].find((r) => r.key === reasonKey) : null;

  const addRecipient = (u) => { if (!recipients.find((r) => r.id === u.id)) setRecipients([...recipients, u]); };
  const removeRecipient = (id) => setRecipients(recipients.filter((r) => r.id !== id));

  const urgencyFromPrio = { critica: "critico", alta: "alto", normale: "normale", bassa: "normale" }[priority];

  const runAi = async () => {
    if (!rawText.trim()) { toast.error("Scrivi o detta prima cosa vuoi comunicare"); return; }
    if (listening) { try { recogRef.current?.stop(); } catch {} }
    setAiBusy(true); setAiWarnings([]);
    try {
      const { data: d } = await api.post("/ai/draft-request", {
        free_text: rawText.trim(),
        hint_category: reasonKey || "generica",
      });
      if (d.subject) setSubject(d.subject);
      if (d.priority) setPriority(d.priority);
      if (d.body) setBody(d.body);
      if (d.structured_data?.patient_first_name && !pfirst) setPfirst(d.structured_data.patient_first_name);
      if (d.structured_data?.patient_last_name && !plast) setPlast(d.structured_data.patient_last_name);
      setAiWarnings(Array.isArray(d.warnings) ? d.warnings : []);
      setAiApplied(true);

      // Prova a riconoscere il destinatario dal testo dettato
      // Estraiamo possibili cognomi: parole capitalizzate dopo "dr", "dott", "prof", "specialista", "laboratorio", "centro"
      // oppure prendiamo i cognomi puri (2+ char maiuscolo iniziale) escludendo il paziente
      if (recipients.length === 0 && synapseKey) {
        const syn = SYNAPSES.find((s) => s.key === synapseKey);
        const excluded = new Set([pfirst, plast, d.structured_data?.patient_first_name, d.structured_data?.patient_last_name].filter(Boolean).map((x) => x.toLowerCase()));
        const raw = rawText;
        // Pattern: "dr(.?) X", "dott(.?) X", "dott.ssa X", "prof(.?) X", "laboratorio X", "centro X"
        const patterns = [
          /(?:dr|dott(?:\.|ore)?|prof(?:\.|essor(?:e|essa)?)?|specialista|laboratorio|centro diagnostico|centro)\.?\s+([A-ZÀ-Ú][a-zà-ú]+(?:\s+[A-ZÀ-Ú][a-zà-ú]+)?)/gi,
        ];
        const candidates = new Set();
        for (const p of patterns) {
          let m; while ((m = p.exec(raw)) !== null) {
            if (m[1] && !excluded.has(m[1].toLowerCase())) candidates.add(m[1].trim());
          }
        }
        const guess = [...candidates][0];
        if (guess) {
          try {
            const roles = syn.key === "diagnostica" ? ["laboratorio", "diagnostica"] : [syn.role];
            let matches = [];
            for (const r of roles) {
              const { data: cs } = await api.get("/contacts", { params: { q: guess, role: r } });
              matches = matches.concat(cs || []);
            }
            if (matches.length === 1) {
              setRecipients([matches[0]]);
              toast.success(`Destinatario riconosciuto: ${matches[0].name || matches[0].last_name || guess}`);
            } else if (matches.length > 1) {
              setQuery(guess);
              toast.info(`Trovati ${matches.length} contatti per "${guess}" — scegli manualmente`);
            } else {
              setQuery(guess);
              toast.info(`Nessun contatto trovato per "${guess}" — compila manualmente`);
            }
          } catch { /* silenzioso */ }
        }
      }
      toast.success("Campi compilati dall'AI — revisiona prima di inviare");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore AI");
    } finally { setAiBusy(false); }
  };

  const submit = async () => {
    const finalBody = (body || rawText).trim();
    if (!subject.trim() || !pfirst.trim() || !plast.trim() || !finalBody || recipients.length === 0) {
      toast.error("Compila oggetto, paziente, messaggio e almeno un destinatario.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: resp } = await api.post("/threads", {
        subject: subject.trim(),
        patient: { first_name: pfirst.trim(), last_name: plast.trim(),
                   birth_date: pbirth || "", internal_ref: pref.trim(),
                   email: pemail.trim() || null },
        recipient_ids: recipients.map((r) => r.id),
        category: reasonKey || "generica",
        urgency: urgencyFromPrio,
        priority,
        body: finalBody,
        send_to_patient: !!(sendToPatient && pemail.trim()),
      });
      toast.success("Sinapsi inviata");
      onOpenChange(false);
      onCreated && onCreated(resp);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio");
    } finally { setSubmitting(false); }
  };

  // Manteniamo la ref al submit aggiornata così i comandi vocali possono chiamarlo
  React.useEffect(() => { submitRef.current = submit; });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="compose-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {step === 1 && "Crea una sinapsi con…"}
            {step === 2 && `Sinapsi con ${currentSynapse?.short} — Motivo?`}
            {step === 3 && `${currentSynapse?.short} · ${currentReason?.label}`}
          </DialogTitle>
          <DialogDescription>
            {step === 1 && "Scegli il collega con cui vuoi connetterti."}
            {step === 2 && "Seleziona il motivo — così l'AI riformula la comunicazione nel tono giusto."}
            {step === 3 && "Scrivi liberamente: l'AI struttura oggetto, priorità e testo. Rivedi prima di inviare."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {SYNAPSES.map((s) => {
              const Icon = s.icon;
              return (
                <button key={s.key}
                        onClick={() => { setSynapseKey(s.key); setReasonKey(null); setStep(2); }}
                        data-testid={`synapse-${s.key}`}
                        className="group text-center p-4 rounded-2xl border border-slate-200 bg-white hover:border-[#1E3A8A]/30 hover:shadow-md transition-all flex flex-col items-center min-h-[150px]">
                  <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${s.color} flex items-center justify-center text-white shadow-sm group-hover:scale-105 transition-transform flex-shrink-0`}>
                    <Icon className="h-7 w-7" strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 flex items-center justify-center mt-4">
                    <div className="font-bold text-slate-900 uppercase whitespace-nowrap text-[10.5px] md:text-[11px] tracking-[0.02em]">{s.label}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {step === 2 && synapseKey && (
          <div className="space-y-2.5">
            {REASONS[synapseKey].map((r) => (
              <button key={r.key} onClick={() => {
                        setReasonKey(r.key);
                        // Precompila l'oggetto con l'etichetta del motivo (l'utente può modificarlo)
                        setSubject((prev) => prev && prev.trim() ? prev : r.label);
                        setStep(3);
                      }}
                      data-testid={`reason-${r.key}`}
                      className="w-full text-left p-3 rounded-xl border border-slate-200 bg-white hover:border-[#1E3A8A]/30 hover:bg-slate-50 transition-all flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#DBEAFE] to-[#D1FAE5] text-[#1E3A8A] flex items-center justify-center font-heading font-bold flex-shrink-0">
                  <ChevronRight className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900">{r.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{r.hint}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            {/* 1) Oggetto + Priorità (Oggetto precompilato col motivo) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label htmlFor="subject">Oggetto</Label>
                <Input id="subject" value={subject} onChange={(e) => setSubject(e.target.value)}
                       placeholder="Es. Richiesta prescrizione — precompilato, modificabile"
                       data-testid="compose-subject" />
              </div>
              <div>
                <Label>Priorità</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger data-testid="compose-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* 2) Destinatari — provincia a SINISTRA, nome a DESTRA. Nessun elenco pre-popolato. */}
            <div>
              <Label>Destinatari · {currentSynapse?.short}</Label>
              <div className="mt-1 border border-slate-200 rounded-lg p-2 min-h-[42px] flex flex-wrap gap-1.5 bg-white">
                {recipients.length === 0 && <span className="text-sm text-slate-400 px-1">Nessun destinatario</span>}
                {recipients.map((r) => (
                  <span key={r.id} data-testid={`recipient-chip-${r.id}`}
                        className="inline-flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full bg-[#DBEAFE] text-[#1E3A8A] text-xs font-medium">
                    <ContactAvatar contact={r} size="sm" />
                    <span className="truncate max-w-[240px]">
                      <span className="font-semibold">{r.name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.email}</span>
                    </span>
                    <button onClick={() => removeRecipient(r.id)} className="hover:text-red-600"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <div className="relative mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                {/* Sinistra: Provincia */}
                <Select value={provinceFilter} onValueChange={setProvinceFilter}>
                  <SelectTrigger data-testid="compose-province-filter">
                    <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-slate-400" /><SelectValue placeholder="Provincia" /></div>
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="all">Tutte le province</SelectItem>
                    {provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                {/* Destra: Nome */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input value={query} onChange={(e) => setQuery(e.target.value)}
                         placeholder={`Cerca ${currentSynapse?.short} per nome…`} className="pl-9" data-testid="compose-search" />
                </div>
              </div>
              {/* Elenco risultati: solo se l'utente sta cercando */}
              {(query.trim() || (provinceFilter && provinceFilter !== "all")) ? (
                <div className="mt-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg bg-white">
                  {contacts.length === 0 && (
                    <div className="p-3 text-sm text-slate-400">Nessun {currentSynapse?.short} trovato per la ricerca</div>
                  )}
                  {contacts.map((c) => (
                    <button key={c.id}
                            onClick={() => { addRecipient(c); setQuery(""); }}
                            data-testid={`contact-add-${c.id}`}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 flex items-center gap-3">
                      <ContactAvatar contact={c} size="md" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {professionalTitle(c.profession || c.role, c.specialization)}
                          {c.order_province ? ` · ${c.order_province}` : ""}
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-[10px] flex-shrink-0">Aggiungi</Badge>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="mt-2 text-[11px] text-slate-500 italic">
                  Inizia a digitare un cognome o seleziona una provincia per vedere i {currentSynapse?.short} disponibili.
                </div>
              )}
            </div>

            {/* 3) Paziente */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Nome paziente</Label>
                <Input value={pfirst} onChange={(e) => setPfirst(e.target.value)} placeholder="Nome" data-testid="compose-patient-first" /></div>
              <div><Label>Cognome paziente</Label>
                <Input value={plast} onChange={(e) => setPlast(e.target.value)} placeholder="Cognome" data-testid="compose-patient-last" /></div>
              <div><Label>Data di nascita (opz.)</Label>
                <Input type="date" value={pbirth} onChange={(e) => setPbirth(e.target.value)} data-testid="compose-patient-birth" /></div>
              <div><Label>Rif. interno (opz.)</Label>
                <Input value={pref} onChange={(e) => setPref(e.target.value)} placeholder="Codice interno" data-testid="compose-patient-ref" /></div>
              <div className="md:col-span-2">
                <Label htmlFor="compose-patient-email">
                  Email paziente <span className="text-slate-400 text-xs">(opz. — per ricevere ricetta/comunicazioni)</span>
                </Label>
                <Input id="compose-patient-email" type="email" value={pemail}
                       onChange={(e) => { const v = e.target.value.trim(); setPemail(v); if (!v) setSendToPatient(false); }}
                       placeholder="paziente@dominio.it"
                       autoComplete="off"
                       data-testid="compose-patient-email" />
                <p className="text-[11px] text-slate-500 mt-1">
                  Se compilata, il MMG potrà inviare la ricetta direttamente al paziente su questa email.
                </p>
                <label className={`mt-2 flex items-start gap-2 p-2.5 rounded-lg border transition-all cursor-pointer ${
                    sendToPatient
                      ? "bg-emerald-50 border-emerald-300"
                      : "bg-white border-slate-200 hover:border-slate-300"
                  } ${!pemail.trim() ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid="compose-send-to-patient-wrap">
                  <input type="checkbox"
                         className="mt-0.5 h-4 w-4 accent-emerald-600 flex-shrink-0"
                         checked={sendToPatient}
                         disabled={!pemail.trim()}
                         onChange={(e) => setSendToPatient(e.target.checked)}
                         data-testid="compose-send-to-patient" />
                  <div className="text-[12px] leading-snug">
                    <span className="font-semibold text-slate-900">Invia copia della comunicazione anche al paziente</span>
                    <p className="text-slate-600 mt-0.5">
                      L'AI riformulerà il testo in tono comprensibile, personalizzato con il nome del paziente,
                      e lo invierà a <span className="font-mono">{pemail || "email paziente"}</span>. Il paziente conserverà così una copia.
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* 4) Scrivi o detta la tua sinapsi (testo libero + AI + voce) */}
            <div className="rounded-xl border border-[#1E3A8A]/20 bg-gradient-to-br from-[#DBEAFE]/40 to-[#D1FAE5]/30 p-3">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Sparkles className="h-4 w-4 text-[#1E3A8A]" />
                <span className="font-semibold text-sm text-slate-800">Scrivi o detta la tua sinapsi</span>
                <span className="text-xs text-slate-500">— l'AI compila oggetto e comunicazione al posto tuo</span>
              </div>
              <div className="relative">
                <Textarea rows={4} value={rawText} onChange={(e) => setRawText(e.target.value)}
                          placeholder="Es. Serve emocromo urgente per Rossi Mario, sospetto anemia. Chiedo integrazione ferritina."
                          data-testid="compose-raw" className="bg-white pr-12" />
                <button type="button" onClick={toggleDictation}
                        data-testid="compose-mic"
                        title={listening ? "Ferma dettatura" : "Detta con il microfono"}
                        className={`absolute right-2 top-2 h-9 w-9 rounded-full flex items-center justify-center transition-all shadow-sm ${
                          listening
                            ? "bg-red-500 text-white ring-4 ring-red-200 animate-pulse"
                            : "bg-white border border-slate-200 text-[#1E3A8A] hover:bg-[#DBEAFE]"
                        }`}>
                  {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </button>
                {!speechSupported && (
                  <button type="button" onClick={toggleWhisperDictation}
                          disabled={whisperBusy}
                          data-testid="compose-mic-whisper"
                          title={whisperRecording ? "Ferma e trascrivi" : "Registra e trascrivi con Whisper"}
                          className={`absolute right-12 top-2 h-9 w-9 rounded-full flex items-center justify-center transition-all shadow-sm ${
                            whisperRecording
                              ? "bg-red-500 text-white ring-4 ring-red-200 animate-pulse"
                              : "bg-white border border-slate-200 text-emerald-700 hover:bg-emerald-50"
                          } disabled:opacity-50 disabled:cursor-not-allowed`}>
                    {whisperBusy ? <SynapseLoader size="sm" inline />
                                 : whisperRecording ? <MicOff className="h-4 w-4" />
                                 : <Mic className="h-4 w-4" />}
                  </button>
                )}
              </div>
              {(listening || whisperRecording || whisperBusy) && (
                <div className="mt-2 text-[11px] font-semibold text-red-600 flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                  {whisperBusy ? "Trascrivo con Whisper…"
                    : whisperRecording ? "Registrazione in corso… tocca il microfono per fermare e trascrivere."
                    : "Sto ascoltando… parla in italiano. Prova i comandi: \"priorità alta\", \"email paziente…\", \"invia sinapsi\"."}
                </div>
              )}
              {!listening && !whisperRecording && !whisperBusy && (
                <div className="mt-2 text-[10px] text-slate-500 leading-relaxed">
                  💡 Comandi vocali: <em>"priorità critica"</em>, <em>"email paziente mario@dominio.it"</em>, <em>"cancella tutto"</em>, <em>"invia sinapsi"</em>.
                  {!speechSupported && " Il tuo browser non supporta Web Speech: usa il tasto verde (Whisper server)."}
                </div>
              )}
              <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                <p className="text-[11px] text-slate-500 italic">
                  L'AI non prescrive né diagnostica. Rivedi sempre prima di inviare.
                </p>
                <Button size="sm" onClick={runAi} disabled={aiBusy || !rawText.trim()}
                        data-testid="compose-ai-run"
                        className="bg-gradient-to-r from-[#1E3A8A] to-[#10B981] hover:from-[#1E40AF] hover:to-[#059669] text-white">
                  {aiBusy ? <><SynapseLoader size="sm" inline className="mr-2" /> Compilo campi…</>
                          : <><Sparkles className="h-4 w-4 mr-1.5" /> Compila con AI</>}
                </Button>
              </div>
              {aiWarnings.length > 0 && (
                <div className="mt-3 text-[11px] text-orange-800 bg-orange-50 border border-orange-200 rounded-md p-2">
                  <div className="font-bold uppercase tracking-wider mb-1">Attenzione (AI)</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {aiWarnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
            </div>

            {/* 5) Comunicazione finale (compilata dall'AI o a mano) — sempre modificabile */}
            <div>
              <Label>Comunicazione da inviare {aiApplied && <Badge variant="secondary" className="ml-2 text-[9px] bg-emerald-50 text-emerald-700 border border-emerald-200">RIFORMULATA AI</Badge>}</Label>
              <Textarea rows={5} value={body} onChange={(e) => { setBody(e.target.value); setAiApplied(false); }}
                        placeholder="Il messaggio finale che verrà inviato al destinatario. Puoi riscriverlo o modificarlo."
                        data-testid="compose-body" />
            </div>

            <div className="text-[11px] text-slate-500 italic border-t border-slate-100 pt-2">
              Synappsi non prende decisioni cliniche e non genera prescrizioni automatiche: la decisione resta responsabilità del medico ricevente.
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 1 && (
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="compose-cancel">Annulla</Button>
          )}
          {step === 2 && (
            <Button variant="outline" onClick={() => setStep(1)} data-testid="compose-back">
              <ChevronLeft className="h-4 w-4 mr-1" /> Cambia collega
            </Button>
          )}
          {step === 3 && (
            <>
              <Button variant="outline" onClick={() => setStep(2)} data-testid="compose-back">
                <ChevronLeft className="h-4 w-4 mr-1" /> Cambia motivo
              </Button>
              <Button onClick={submit} disabled={submitting} data-testid="compose-submit"
                      className="h-10 rounded-xl bg-gradient-to-r from-[#1E3A8A] via-[#2563EB] to-[#10B981]
                                 hover:from-[#1E40AF] hover:via-[#3B82F6] hover:to-[#059669]
                                 text-white font-semibold shadow-md shadow-emerald-500/10">
                {submitting ? <><SynapseLoader size="sm" inline className="mr-2" /> Invio…</> : "Invia sinapsi"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
